import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  coursesApi,
  modulesApi,
  enrollmentApi,
  quizApi,
  gamificationApi,
  Course,
  authApi,
} from "../lib";
import { showError, showSuccess, showInfo } from "../lib/toast";
import {
  ArrowLeft,
  ArrowRight,
  Play,
  FileText,
  Code,
  HelpCircle,
  CheckSquare,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  BookOpen,
  Loader,
  Trophy,
  Award,
  Share2,
  Bookmark,
  ChevronRight,
  Maximize2,
  Minimize2,
  SkipBack,
  SkipForward,
  Settings,
  Sparkles,
  Star,
  Zap,
  User,
  Search,
  XCircle,
  RotateCcw,
  Flame,
  Target,
  Crown,
  Medal,
  ScrollText,
  IceCream,
  Gift,
} from "lucide-react";

interface GamificationState {
  xp: number;
  level: number;
  streak: number;
  lastActivityDate: string;
  achievements: Achievement[];
  totalLessonsCompleted: number;
  totalQuizzesPassed: number;
  totalCourseTime: number;
  weeklyXP: number;
  rank: number;
  dailyQuests: any[];
  xpTransactions: any[];
  hasStreakFreeze: boolean;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlockedAt?: string;
  progress: number;
  target: number;
  xpReward: number;
}

const XP_PER_LESSON = 50;
const XP_PER_QUIZ = 100;
const XP_PER_STREAK = 25;
const XP_PERFECT_QUIZ_BONUS = 50;
const LEVELS = [
  { level: 1, minXP: 0, title: "Novice Learner" },
  { level: 2, minXP: 200, title: "Apprentice" },
  { level: 3, minXP: 500, title: "Student" },
  { level: 4, minXP: 1000, title: "Scholar" },
  { level: 5, minXP: 2000, title: "Expert" },
  { level: 6, minXP: 3500, title: "Master" },
  { level: 7, minXP: 5500, title: "Guru" },
  { level: 8, minXP: 8000, title: "Legend" },
  { level: 9, minXP: 12000, title: "Champion" },
  { level: 10, minXP: 17000, title: "Grandmaster" },
];

const INITIAL_ACHIEVEMENTS: Achievement[] = [
  {
    id: "first_lesson",
    title: "First Steps",
    description: "Complete your first lesson",
    icon: "rocket",
    progress: 0,
    target: 1,
    xpReward: 100,
  },
  {
    id: "five_lessons",
    title: "Getting Started",
    description: "Complete 5 lessons",
    icon: "star",
    progress: 0,
    target: 5,
    xpReward: 200,
  },
  {
    id: "ten_lessons",
    title: "Dedicated Learner",
    description: "Complete 10 lessons",
    icon: "trophy",
    progress: 0,
    target: 10,
    xpReward: 300,
  },
  {
    id: "first_quiz",
    title: "Quiz Taker",
    description: "Pass your first quiz",
    icon: "target",
    progress: 0,
    target: 1,
    xpReward: 150,
  },
  {
    id: "three_quizzes",
    title: "Quiz Master",
    description: "Pass 3 quizzes",
    icon: "award",
    progress: 0,
    target: 3,
    xpReward: 350,
  },
  {
    id: "streak_3",
    title: "On Fire",
    description: "Maintain a 3-day streak",
    icon: "flame",
    progress: 0,
    target: 3,
    xpReward: 200,
  },
  {
    id: "streak_7",
    title: "Week Warrior",
    description: "Maintain a 7-day streak",
    icon: "crown",
    progress: 0,
    target: 7,
    xpReward: 500,
  },
  {
    id: "streak_30",
    title: "Unstoppable",
    description: "Maintain a 30-day streak",
    icon: "medal",
    progress: 0,
    target: 30,
    xpReward: 1000,
  },
  {
    id: "half_course",
    title: "Halfway There",
    description: "Complete 50% of a course",
    icon: "target",
    progress: 0,
    target: 50,
    xpReward: 400,
  },
  {
    id: "full_course",
    title: "Course Complete",
    description: "Complete an entire course",
    icon: "crown",
    progress: 0,
    target: 100,
    xpReward: 750,
  },
];

// ============ TYPES ============
interface Lesson {
  id: string;
  title: string;
  type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
  duration: number;
  isPreview: boolean;
  videoUrl?: string;
  videoDuration?: number;
  videoTranscript?: string;
  content?: string;
  bullets?: string;
  topics?: any[];
  paragraphs?: any[];
  images?: any[];
  links?: any[];
  codeBlocks?: any[];
}

interface Module {
  id: string;
  title: string;
  description: string;
  lessons: Lesson[];
  quiz?: any;
  hasQuiz?: boolean;
}

// ============ UTILITIES ============
const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const getLevelInfo = (xp: number) => {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].minXP) {
      const nextLevel = LEVELS[i + 1];
      return {
        current: LEVELS[i],
        next: nextLevel,
        progress: nextLevel
          ? ((xp - LEVELS[i].minXP) / (nextLevel.minXP - LEVELS[i].minXP)) * 100
          : 100,
      };
    }
  }
  return {
    current: LEVELS[0],
    next: LEVELS[1],
    progress: (xp / LEVELS[1].minXP) * 100,
  };
};

const getAchievementIcon = (iconName: string) => {
  const icons: Record<string, JSX.Element> = {
    rocket: <Rocket className="w-5 h-5" />,
    star: <Star className="w-5 h-5" />,
    trophy: <Trophy className="w-5 h-5" />,
    target: <Target className="w-5 h-5" />,
    award: <Award className="w-5 h-5" />,
    flame: <Flame className="w-5 h-5" />,
    crown: <Crown className="w-5 h-5" />,
    medal: <Medal className="w-5 h-5" />,
  };
  return icons[iconName] || <Star className="w-5 h-5" />;
};

interface AchievementNotificationProps {
  achievement: Achievement;
  onClose: () => void;
}

const AchievementNotification = ({
  achievement,
  onClose,
}: AchievementNotificationProps) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed z-50 bottom-4 right-4 animate-slide-up md:bottom-6 md:right-6">
      <div className="flex items-center gap-3 p-3 border shadow-xl bg-gradient-to-r from-orange-500/90 to-amber-500/90 backdrop-blur-sm rounded-xl border-orange-400/50 md:gap-4 md:p-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 md:w-12 md:h-12">
          {getAchievementIcon(achievement.icon)}
        </div>
        <div>
          <p className="text-xs font-bold text-white md:text-sm">
            Achievement Unlocked!
          </p>
          <p className="text-xs text-white/90 md:text-sm">
            {achievement.title}
          </p>
          <p className="text-[10px] text-white/70 md:text-xs">
            +{achievement.xpReward} XP
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 transition-colors rounded-lg hover:bg-white/20"
        >
          <X className="w-3 h-3 text-white md:w-4 md:h-4" />
        </button>
      </div>
    </div>
  );
};

const LevelUpNotification = ({
  level,
  title,
  onClose,
}: {
  level: number;
  title: string;
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 6000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative max-w-sm p-6 mx-4 text-center border shadow-2xl bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600 rounded-2xl border-white/20 md:p-8">
        <div className="absolute -translate-x-1/2 -top-12 left-1/2 md:-top-16">
          <div className="relative">
            <div className="flex items-center justify-center w-16 h-16 rounded-full shadow-lg bg-gradient-to-br from-yellow-400 to-amber-500 animate-bounce md:w-20 md:h-20">
              <Crown className="w-8 h-8 text-white md:w-10 md:h-10" />
            </div>
            <div className="absolute px-2 py-0.5 -translate-x-1/2 rounded-full -bottom-2 left-1/2 bg-gradient-to-r from-yellow-400 to-amber-500 md:px-3 md:py-1">
              <span className="text-sm font-bold text-white md:text-base">
                LVL {level}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-10 md:mt-14">
          <h3 className="mb-1 text-xl font-bold text-white md:text-2xl">
            Level Up!
          </h3>
          <p className="mb-2 text-base text-white/90 md:text-lg">{title}</p>
          <p className="text-xs text-white/70 md:text-sm">
            Keep up the great work!
          </p>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-1.5 mt-4 text-xs text-white transition-colors rounded-lg bg-white/20 hover:bg-white/30 md:px-6 md:py-2 md:text-sm"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
};

const StreakNotification = ({
  streak,
  onClose,
}: {
  streak: number;
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed z-50 bottom-4 right-4 animate-slide-up md:bottom-6 md:right-6">
      <div className="flex items-center gap-3 p-3 border shadow-xl bg-gradient-to-r from-orange-500/90 to-red-500/90 backdrop-blur-sm rounded-xl border-orange-400/50 md:gap-4 md:p-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 animate-pulse md:w-12 md:h-12">
          <Flame className="w-5 h-5 text-white md:w-6 md:h-6" />
        </div>
        <div>
          <p className="text-sm font-bold text-white md:text-base">
            {streak} Day Streak!
          </p>
          <p className="text-xs text-white/90 md:text-sm">
            +{XP_PER_STREAK} XP bonus
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 transition-colors rounded-lg hover:bg-white/20"
        >
          <X className="w-3 h-3 text-white md:w-4 md:h-4" />
        </button>
      </div>
    </div>
  );
};

const GamificationPanel = ({
  gamification,
  onClose,
}: {
  gamification: GamificationState;
  onClose: () => void;
}) => {
  const levelInfo = getLevelInfo(gamification.xp);
  const today = new Date().toISOString().split("T")[0];
  const isActiveToday = gamification.lastActivityDate === today;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-gray-900 rounded-2xl border border-gray-700/50 shadow-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50 bg-gradient-to-r from-orange-500/20 to-amber-500/20">
          <h2 className="flex items-center gap-2 text-base font-bold text-white md:text-xl">
            <Rocket className="w-4 h-4 text-orange-500 md:w-5 md:h-5" />
            Your Progress
          </h2>
          <button
            onClick={onClose}
            className="p-2 transition-colors rounded-lg hover:bg-white/10"
          >
            <X className="w-4 h-4 text-gray-400 md:w-5 md:h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)] md:p-6 md:space-y-6">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="p-3 border bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-xl border-orange-500/30 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-3 h-3 text-orange-500 md:w-4 md:h-4" />
                <span className="text-[10px] text-gray-400 md:text-xs">
                  Level
                </span>
              </div>
              <p className="text-lg font-bold text-white md:text-2xl">
                {gamification.level}
              </p>
              <p className="text-[10px] text-orange-400 md:text-xs">
                {levelInfo.current.title}
              </p>
            </div>
            <div className="p-3 border bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl border-amber-500/30 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-3 h-3 text-amber-500 md:w-4 md:h-4" />
                <span className="text-[10px] text-gray-400 md:text-xs">
                  Total XP
                </span>
              </div>
              <p className="text-lg font-bold text-white md:text-2xl">
                {gamification.xp}
              </p>
              {levelInfo.next && (
                <p className="text-[10px] text-amber-400 md:text-xs">
                  {levelInfo.next.minXP - gamification.xp} XP to next level
                </p>
              )}
            </div>
            <div className="p-3 border bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl border-orange-500/30 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-3 h-3 text-orange-500 md:w-4 md:h-4" />
                <span className="text-[10px] text-gray-400 md:text-xs">
                  Streak
                </span>
              </div>
              <p className="text-lg font-bold text-white md:text-2xl">
                {gamification.streak}
              </p>
              <p className="text-[10px] text-orange-400 md:text-xs">
                {isActiveToday ? "Active today!" : "Start today!"}
              </p>
            </div>
            <div className="p-3 border bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl border-emerald-500/30 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="w-3 h-3 text-emerald-500 md:w-4 md:h-4" />
                <span className="text-[10px] text-gray-400 md:text-xs">
                  Rank
                </span>
              </div>
              <p className="text-lg font-bold text-white md:text-2xl">
                #{gamification.rank}
              </p>
              <p className="text-[10px] text-emerald-400 md:text-xs">Top 10%</p>
            </div>
          </div>

          <div className="p-3 border bg-gray-800/50 rounded-xl border-gray-700/30 md:p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-white md:text-sm">
                Level Progress
              </span>
              <span className="text-xs font-bold text-orange-500 md:text-sm">
                {Math.round(levelInfo.progress)}%
              </span>
            </div>
            <div className="h-2 overflow-hidden bg-gray-700 rounded-full md:h-3">
              <div
                className="h-full transition-all duration-500 rounded-full bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500"
                style={{ width: `${levelInfo.progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-[9px] text-gray-500 md:text-xs">
              <span>{levelInfo.current.minXP} XP</span>
              <span>{levelInfo.next?.minXP || "MAX"} XP</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center md:gap-3">
            <div className="p-2 rounded-lg bg-gray-800/30 md:p-3">
              <p className="text-sm font-bold text-white md:text-base">
                {gamification.totalLessonsCompleted}
              </p>
              <p className="text-[9px] text-gray-400 md:text-xs">Lessons</p>
            </div>
            <div className="p-2 rounded-lg bg-gray-800/30 md:p-3">
              <p className="text-sm font-bold text-white md:text-base">
                {gamification.totalQuizzesPassed}
              </p>
              <p className="text-[9px] text-gray-400 md:text-xs">Quizzes</p>
            </div>
            <div className="p-2 rounded-lg bg-gray-800/30 md:p-3">
              <p className="text-sm font-bold text-white md:text-base">
                {Math.round(gamification.totalCourseTime / 60)}h
              </p>
              <p className="text-[9px] text-gray-400 md:text-xs">Time</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-white md:text-base md:mb-4">
                <ScrollText className="w-4 h-4 text-orange-500 md:w-5 md:h-5" />
                Daily Quests
              </h3>
              <button
                onClick={async () => {
                  try {
                    await gamificationApi.assignDailyQuests();
                    await fetchGamificationExtras();
                    showSuccess("Quests refreshed");
                  } catch (err) {
                    showError("Failed to refresh quests");
                  }
                }}
                className="text-[10px] text-orange-400 hover:text-orange-300"
              >
                Refresh
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2 md:gap-3">
              {gamification.dailyQuests.length === 0 ? (
                <div className="p-3 text-center border rounded-xl border-gray-700/40 bg-gray-800/30">
                  <p className="text-[11px] text-gray-400">No active quests</p>
                  <button
                    onClick={async () => {
                      try {
                        await gamificationApi.assignDailyQuests();
                        await fetchGamificationExtras();
                      } catch (err) {
                        showError("Failed to assign quests");
                      }
                    }}
                    className="mt-2 text-[10px] text-orange-400 hover:text-orange-300"
                  >
                    Assign quests
                  </button>
                </div>
              ) : (
                gamification.dailyQuests.map((q: any) => {
                  const quest = q.quest || q;
                  const progress = q.progress ?? 0;
                  const target = q.target ?? quest.requirement?.count ?? 1;
                  const status = q.status ?? "active";
                  const percent = Math.min(100, Math.round((progress / target) * 100));

                  return (
                    <div
                      key={q._id || quest._id}
                      className={`p-3 rounded-xl border transition-all md:p-4 ${
                        status === "completed"
                          ? "bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30"
                          : "bg-gray-800/30 border-gray-700/30"
                      }`}
                    >
                      <div className="flex items-start gap-2 md:gap-3">
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                            status === "completed"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-gray-700 text-gray-400"
                          } md:w-10 md:h-10`}
                        >
                          <span className="text-base">{quest.icon || "🎯"}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-xs font-medium ${status === "completed" ? "text-white" : "text-gray-300"} md:text-sm`}
                          >
                            {quest.title}
                          </p>
                          <p className="text-[10px] text-gray-500 md:text-xs">
                            {quest.description}
                          </p>
                          <div className="h-1 mt-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-[9px] text-gray-500">
                              {progress}/{target}
                            </p>
                            <p className="text-[9px] text-amber-400">
                              +{quest.xpReward || 0} XP
                              {quest.bonusReward?.xp
                                ? ` (+${quest.bonusReward.xp} bonus)`
                                : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="mt-4">
            <h3 className="flex items-center gap-2 mb-3 text-sm font-bold text-white md:text-base md:mb-4">
              <Zap className="w-4 h-4 text-amber-500 md:w-5 md:h-5" />
              Recent XP
            </h3>
            <div className="space-y-2">
              {gamification.xpTransactions.length === 0 ? (
                <p className="text-[11px] text-center text-gray-500">
                  No recent XP activity
                </p>
              ) : (
                gamification.xpTransactions.map((tx: any) => (
                  <div
                    key={tx._id}
                    className="flex items-center justify-between p-2 border rounded-lg bg-gray-800/40 border-gray-700/40"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center text-[11px]">
                        {tx.source === "lesson_completed"
                          ? "📘"
                          : tx.source === "quiz_passed"
                            ? "✅"
                            : tx.source === "quest_completed"
                              ? "🎯"
                              : tx.source === "badge_earned"
                                ? "🏆"
                                : "✨"}
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-gray-300">
                          {tx.description || tx.source}
                        </p>
                        <p className="text-[9px] text-gray-500">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-amber-400">
                      +{tx.amount}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ COMPONENTS ============
const LessonIcon = ({
  type,
  isCompleted = false,
  isCurrent = false,
  className = "w-4 h-4 md:w-5 md:h-5",
}: {
  type: string;
  isCompleted?: boolean;
  isCurrent?: boolean;
  className?: string;
}) => {
  if (isCompleted)
    return (
      <CheckCircle className={`${className} text-emerald-500 drop-shadow-md`} />
    );
  if (isCurrent)
    return (
      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse md:w-2 md:h-2" />
    );

  const icons: Record<string, JSX.Element> = {
    video: <Play className={`${className} text-blue-500`} />,
    text: <FileText className={`${className} text-green-500`} />,
    code: <Code className={`${className} text-purple-500`} />,
    quiz: <HelpCircle className={`${className} text-amber-500`} />,
    assignment: <CheckSquare className={`${className} text-teal-500`} />,
    mixed: <BookOpen className={`${className} text-orange-500`} />,
  };
  return icons[type] || <BookOpen className={`${className} text-gray-500`} />;
};

const getEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  if (url.includes("youtube.com/watch")) {
    const match = url.match(/[?&]v=([^&]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  if (url.includes("youtu.be/")) {
    const match = url.match(/youtu\.be\/([^?]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  if (url.includes("youtube.com/embed/")) return url;
  if (url.includes("vimeo.com/"))
    return url.replace("vimeo.com", "player.vimeo.com/video");
  return url;
};

// Video Player Component
const VideoPlayer = ({ url, title }: { url: string; title: string }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const embedUrl = getEmbedUrl(url);

  useEffect(() => {
    const handleFullscreenChange = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const container = document.getElementById("video-container");
    if (!isFullscreen) {
      container?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (!embedUrl) return null;

  return (
    <div
      id="video-container"
      className="relative overflow-hidden transition-all duration-300 bg-black border shadow-xl rounded-xl border-gray-700/50 hover:border-orange-500/50 group md:rounded-2xl"
    >
      <div className="relative aspect-video">
        <iframe
          src={embedUrl}
          title={title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 transition-opacity duration-300 md:p-4 ${
            showControls ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 md:gap-3">
              <button className="p-1.5 transition-colors rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm md:p-2">
                <SkipBack className="w-3 h-3 text-white md:w-4 md:h-4" />
              </button>
              <button className="p-1.5 transition-colors rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm md:p-2">
                <Play className="w-3 h-3 text-white md:w-4 md:h-4" />
              </button>
              <button className="p-1.5 transition-colors rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm md:p-2">
                <SkipForward className="w-3 h-3 text-white md:w-4 md:h-4" />
              </button>
              <span className="ml-1 font-mono text-[10px] text-white md:text-sm md:ml-2">
                0:00 / 0:00
              </span>
            </div>
            <button
              onClick={toggleFullscreen}
              className="p-1.5 transition-colors rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm md:p-2"
            >
              {isFullscreen ? (
                <Minimize2 className="w-3 h-3 text-white md:w-4 md:h-4" />
              ) : (
                <Maximize2 className="w-3 h-3 text-white md:w-4 md:h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function CourseLearningPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Check authorization early
  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        const userResponse = await authApi.getCurrentUser();
        if (!userResponse?.success || !userResponse.data) {
          navigate("/login");
          return;
        }
        const currentUser = userResponse.data;
        const courseData = await coursesApi.getCourseById(id);
        const course = courseData.data;

        let enrollment = null;
        try {
          const enrollmentResponse =
            await enrollmentApi.getCourseEnrollment(id);
          enrollment = enrollmentResponse.data;
        } catch (enrollmentError: any) {
          if (
            enrollmentError.message?.includes("not enrolled") ||
            enrollmentError.message?.includes("404")
          ) {
            showError(
              "You are not enrolled in this course. Please enroll first.",
            );
            navigate("/courses");
            return;
          }
          console.warn("Enrollment check error:", enrollmentError);
        }

        const isAdmin = currentUser.role === "admin";
        const isInstructor =
          String(course.instructor._id) === String(currentUser._id);
        const isEnrolled =
          enrollment &&
          (enrollment.status === "active" || enrollment.status === "completed");

        if (!isAdmin && !isInstructor && !isEnrolled) {
          showError(
            "You don't have access to this course. Please enroll first.",
          );
          navigate("/courses");
          return;
        }
      } catch (error: any) {
        console.error("Authorization check failed:", error);
        const errorMsg = error.message || "";
        if (
          errorMsg.includes("401") ||
          errorMsg.includes("token") ||
          errorMsg.includes("unauthorized")
        ) {
          showError("Session expired. Please log in again.");
          navigate("/login");
        } else {
          showError("Failed to verify access. Please try again.");
          navigate("/courses");
        }
      }
    };

    checkAuthorization();
  }, [id, navigate]);

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(
    new Set(),
  );
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set(),
  );
  const [completing, setCompleting] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<any>(null);
  const [quizResults, setQuizResults] = useState<Record<string, any>>({});
  const [gamification, setGamification] = useState<GamificationState>({
    xp: 0,
    level: 1,
    streak: 0,
    lastActivityDate: "",
    achievements: [...INITIAL_ACHIEVEMENTS],
    totalLessonsCompleted: 0,
    totalQuizzesPassed: 0,
    totalCourseTime: 0,
    weeklyXP: 0,
    rank: 1,
    dailyQuests: [],
    xpTransactions: [],
    hasStreakFreeze: false,
  });
  const [showAchievement, setShowAchievement] = useState<Achievement | null>(
    null,
  );
  const [showLevelUp, setShowLevelUp] = useState<{
    level: number;
    title: string;
  } | null>(null);
  const [showStreak, setShowStreak] = useState<number | null>(null);
  const [showGamificationPanel, setShowGamificationPanel] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [user, setUser] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    avatar?: string;
  } | null>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  // Responsive sidebar: start closed on mobile, open on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userRes = await authApi.getCurrentUser();
        if (userRes.success && userRes.data) setUser(userRes.data);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchGamificationStats = async () => {
      try {
        const res = await gamificationApi.getMyStats();
        if (res.success && res.data) {
          const data = res.data;
          const stats = data.stats || {};
          const levelInfo = data.level || { level: 1, title: "Newcomer" };

          setGamification((prev) => ({
            ...prev,
            xp: stats.totalPoints || 0,
            level: levelInfo.level || 1,
            streak: stats.currentStreak || 0,
            lastActivityDate: stats.lastActivityDate || "",
            totalLessonsCompleted: stats.lessonsWatched || 0,
            totalQuizzesPassed: stats.quizzesPassed || 0,
            rank: stats.rank || 0,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch gamification stats:", err);
      }
    };
    fetchGamificationStats();
  }, []);

  const fetchGamificationExtras = async () => {
    try {
      const [questsRes, xpStatsRes, xpTransRes, streakFreezeRes] =
        await Promise.allSettled([
          gamificationApi.getDailyQuests(),
          gamificationApi.getUserXPStats(),
          gamificationApi.getXPTransactions(10),
          gamificationApi.getStreakFreezeStatus(),
        ]);

      if (questsRes.status === "fulfilled" && questsRes.value.success) {
        setGamification((prev) => ({
          ...prev,
          dailyQuests: questsRes.value.data || [],
        }));
      }
      if (xpStatsRes.status === "fulfilled" && xpStatsRes.value.success) {
        setGamification((prev) => ({
          ...prev,
          weeklyXP: xpStatsRes.value.data.weeklyXP || 0,
        }));
      }
      if (xpTransRes.status === "fulfilled" && xpTransRes.value.success) {
        setGamification((prev) => ({
          ...prev,
          xpTransactions: xpTransRes.value.data || [],
        }));
      }
      if (
        streakFreezeRes.status === "fulfilled" &&
        streakFreezeRes.value.success
      ) {
        setGamification((prev) => ({
          ...prev,
          hasStreakFreeze: streakFreezeRes.value.data.hasFreeze || false,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch gamification extras:", err);
    }
  };

  useEffect(() => {
    fetchGamificationExtras();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const courseRes = await coursesApi.getCourseById(id);
        if (!courseRes?.success) {
          showError("Course not found");
          navigate("/");
          return;
        }
        setCourse(courseRes.data);

        const modulesRes = await modulesApi.getCourseModules(id);
        if (modulesRes.success && modulesRes.data) {
          const mappedModules = modulesRes.data.map((mod: any) => ({
            id: mod._id,
            title: mod.title,
            description: mod.description || "",
            lessons:
              mod.lessons?.map((les: any) => ({
                id: les._id || les.id,
                title: les.title,
                type: les.type,
                duration: les.duration || 0,
                isPreview: les.isPreview,
                videoUrl: les.videoUrl,
                videoDuration: les.videoDuration,
                videoTranscript: les.videoTranscript,
                content: les.content,
                bullets: les.bullets,
                topics: les.topics || [],
                paragraphs: les.paragraphs || [],
                images: les.images || [],
                links: les.links || [],
                codeBlocks: les.codeBlocks || [],
              })) || [],
            quiz: mod.quiz || null,
            hasQuiz: mod.hasQuiz || false,
          }));
          setModules(mappedModules);
          if (mappedModules.length > 0)
            setExpandedModules(new Set([mappedModules[0].id]));

          try {
            const enrollmentRes = await enrollmentApi.getCourseEnrollment(id);
            if (enrollmentRes.success && enrollmentRes.data) {
              const completed = enrollmentRes.data.completedLessons || [];
              const completedIds = completed.map((l: any) => l._id || l);
              setCompletedLessons(new Set(completedIds));
              setEnrollmentData(enrollmentRes.data);
            }
          } catch (e) {
            console.log("Not enrolled");
          }
        }
      } catch (err) {
        console.error(err);
        showError("Failed to load course");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Set current lesson when modules load
  useEffect(() => {
    if (loading || !modules.length) return;

    const lastLessonId = enrollmentData?.lastAccessedLesson;

    if (lastLessonId && typeof lastLessonId === "string") {
      for (const mod of modules) {
        const lessons = mod.lessons || [];
        for (const lesson of lessons) {
          const lid = lesson._id || lesson.id;
          if (lid === lastLessonId) {
            setCurrentLesson(lesson);
            setCurrentModule(mod);
            setExpandedModules((s) => new Set([...s, mod.id]));
            return;
          }
        }
      }
    }

    // Find first incomplete lesson
    for (const mod of modules) {
      for (const lesson of mod.lessons || []) {
        const lid = lesson._id || lesson.id;
        if (!completedLessons.has(lid)) {
          setCurrentLesson(lesson);
          setCurrentModule(mod);
          setExpandedModules((s) => new Set([...s, mod.id]));
          return;
        }
      }
    }

    // Default: first lesson
    if (modules[0]?.lessons?.length) {
      setCurrentLesson(modules[0].lessons[0]);
      setCurrentModule(modules[0]);
      setExpandedModules(new Set([modules[0].id]));
    }
  }, [loading, modules, completedLessons, enrollmentData]);

  const checkAndUpdateAchievements = useCallback(
    (type: "lesson" | "quiz" | "streak", value: number): Achievement[] => {
      const newUnlocks: Achievement[] = [];
      const today = new Date().toISOString().split("T")[0];

      setGamification((prev) => {
        const updated = { ...prev };

        if (type === "lesson") {
          updated.totalLessonsCompleted += 1;
          const firstLesson = updated.achievements.find(
            (a) => a.id === "first_lesson",
          );
          if (firstLesson && !firstLesson.unlockedAt) {
            firstLesson.progress = Math.min(1, value);
            if (value >= 1) {
              firstLesson.unlockedAt = today;
              firstLesson.xpReward && (updated.xp += firstLesson.xpReward);
              newUnlocks.push(firstLesson);
            }
          }
          const fiveLessons = updated.achievements.find(
            (a) => a.id === "five_lessons",
          );
          if (fiveLessons && !fiveLessons.unlockedAt) {
            fiveLessons.progress = Math.min(5, value);
            if (value >= 5) {
              fiveLessons.unlockedAt = today;
              fiveLessons.xpReward && (updated.xp += fiveLessons.xpReward);
              newUnlocks.push(fiveLessons);
            }
          }
          const tenLessons = updated.achievements.find(
            (a) => a.id === "ten_lessons",
          );
          if (tenLessons && !tenLessons.unlockedAt) {
            tenLessons.progress = Math.min(10, value);
            if (value >= 10) {
              tenLessons.unlockedAt = today;
              tenLessons.xpReward && (updated.xp += tenLessons.xpReward);
              newUnlocks.push(tenLessons);
            }
          }
        }

        if (type === "quiz") {
          updated.totalQuizzesPassed += 1;
          const firstQuiz = updated.achievements.find(
            (a) => a.id === "first_quiz",
          );
          if (firstQuiz && !firstQuiz.unlockedAt) {
            firstQuiz.progress = 1;
            firstQuiz.unlockedAt = today;
            firstQuiz.xpReward && (updated.xp += firstQuiz.xpReward);
            newUnlocks.push(firstQuiz);
          }
          const threeQuizzes = updated.achievements.find(
            (a) => a.id === "three_quizzes",
          );
          if (threeQuizzes && !threeQuizzes.unlockedAt) {
            threeQuizzes.progress = Math.min(3, value);
            if (value >= 3) {
              threeQuizzes.unlockedAt = today;
              threeQuizzes.xpReward && (updated.xp += threeQuizzes.xpReward);
              newUnlocks.push(threeQuizzes);
            }
          }
        }

        if (type === "streak") {
          const streak3 = updated.achievements.find((a) => a.id === "streak_3");
          if (streak3 && !streak3.unlockedAt) {
            streak3.progress = Math.min(3, value);
            if (value >= 3) {
              streak3.unlockedAt = today;
              streak3.xpReward && (updated.xp += streak3.xpReward);
              newUnlocks.push(streak3);
            }
          }
          const streak7 = updated.achievements.find((a) => a.id === "streak_7");
          if (streak7 && !streak7.unlockedAt) {
            streak7.progress = Math.min(7, value);
            if (value >= 7) {
              streak7.unlockedAt = today;
              streak7.xpReward && (updated.xp += streak7.xpReward);
              newUnlocks.push(streak7);
            }
          }
          const streak30 = updated.achievements.find(
            (a) => a.id === "streak_30",
          );
          if (streak30 && !streak30.unlockedAt) {
            streak30.progress = Math.min(30, value);
            if (value >= 30) {
              streak30.unlockedAt = today;
              streak30.xpReward && (updated.xp += streak30.xpReward);
              newUnlocks.push(streak30);
            }
          }
        }

        const prevLevel = getLevelInfo(updated.xp).current.level;
        for (let i = 0; i < LEVELS.length; i++) {
          if (updated.xp >= LEVELS[i].minXP) {
            updated.level = LEVELS[i].level;
          }
        }
        const newLevel = getLevelInfo(updated.xp).current.level;
        if (newLevel > prevLevel) {
          const levelInfo = getLevelInfo(updated.xp);
          setTimeout(
            () =>
              setShowLevelUp({
                level: newLevel,
                title: levelInfo.current.title,
              }),
            500,
          );
        }

        updated.lastActivityDate = today;
        return updated;
      });

      return newUnlocks;
    },
    [],
  );

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const yesterday = new Date(Date.now() - 86400000)
      .toISOString()
      .split("T")[0];

    if (gamification.lastActivityDate === yesterday) {
      setGamification((prev) => {
        const newStreak = prev.streak + 1;
        setTimeout(() => setShowStreak(newStreak), 1000);
        return { ...prev, streak: newStreak };
      });
    } else if (gamification.lastActivityDate !== today) {
      setGamification((prev) => ({ ...prev, streak: 0 }));
    }
  }, [gamification.lastActivityDate]);

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const n = new Set(prev);
      n.has(moduleId) ? n.delete(moduleId) : n.add(moduleId);
      return n;
    });
  };

  const handleLessonSelect = (lesson: Lesson, module: Module) => {
    if (lesson.type === "quiz") {
      const quizModule = modules.find((m) => m.hasQuiz || m.quiz);
      if (quizModule && id) {
        const quizUrl = `/course/${id}/module/${quizModule.id}/take-quiz`;
        window.open(
          quizUrl,
          "_blank",
          "width=900,height=700,resizable=yes,scrollbars=yes",
        );
      }
      return;
    }

    setCurrentLesson(lesson);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
    setCurrentModule(module);
    setSidebarOpen(false);

    if (id && lesson.id !== "quiz")
      enrollmentApi.updateLastAccessed(id, lesson.id).catch(console.error);
  };

  const handleCompleteLesson = async () => {
    if (!id || !currentLesson || completedLessons.has(currentLesson.id)) return;

    if (currentLesson.type === "quiz") {
      showSuccess("🎉 Quiz completed!");
      setCompletedLessons((prev) => new Set(prev).add(currentLesson.id));
      return;
    }

    setCompleting(true);
    try {
      const response = await enrollmentApi.completeLesson(id, currentLesson.id);
      if (response.success && response.data?.completedLessons) {
        const completedIds = response.data.completedLessons.map(
          (l: any) => l._id || l,
        );
        setCompletedLessons(new Set(completedIds));
      } else {
        setCompletedLessons((prev) => new Set(prev).add(currentLesson.id));
      }

      if (user?._id) {
        try {
          const gamRes = await gamificationApi.completeLesson(
            user._id,
            currentLesson.id,
          );
          if (gamRes.success && gamRes.data) {
            setGamification((prev) => ({
              ...prev,
              xp: gamRes.data.totalPoints || prev.xp + XP_PER_LESSON,
              level: gamRes.data.level || prev.level,
              streak: gamRes.data.currentStreak || prev.streak,
              totalLessonsCompleted: gamRes.data.lessonsWatched || 0,
            }));
          }
          if (gamRes.newBadges && gamRes.newBadges.length > 0) {
            setTimeout(
              () =>
                setShowAchievement({
                  id: gamRes.newBadges[0]._id,
                  title: gamRes.newBadges[0].name,
                  description: gamRes.newBadges[0].description,
                  icon: gamRes.newBadges[0].icon,
                  progress: gamRes.newBadges[0].requirement?.count || 1,
                  target: gamRes.newBadges[0].requirement?.count || 1,
                  xpReward: gamRes.newBadges[0].points,
                }),
              1500,
            );
          }
          showSuccess(`🎉 Lesson completed! +${XP_PER_LESSON} XP`);
          fetchGamificationExtras();
        } catch (gamErr) {
          console.error("Failed to update gamification:", gamErr);
          showSuccess(`🎉 Lesson completed! +${XP_PER_LESSON} XP`);
          fetchGamificationExtras();
        }
      } else {
        showSuccess(`🎉 Lesson completed! +${XP_PER_LESSON} XP`);
        fetchGamificationExtras();
      }

      const nextLesson = getNextLesson();
      if (nextLesson) {
        setTimeout(() => {
          const nextModule = modules.find((m) =>
            m.lessons.some((l) => l.id === nextLesson.id),
          );
          if (nextModule) handleLessonSelect(nextLesson, nextModule);
        }, 1000);
      }
    } catch (err: any) {
      showError(err.message || "Could not mark lesson complete");
    } finally {
      setCompleting(false);
    }
  };

  const getNextLesson = () => {
    if (!currentModule || !currentLesson) return null;
    const currentModuleIndex = modules.findIndex(
      (m) => m.id === currentModule.id,
    );
    const currentLessonIndex = currentModule.lessons.findIndex(
      (l) => l.id === currentLesson.id,
    );
    if (currentLessonIndex < currentModule.lessons.length - 1)
      return currentModule.lessons[currentLessonIndex + 1];
    if (currentModuleIndex < modules.length - 1)
      return modules[currentModuleIndex + 1].lessons[0];

    if (currentModule.hasQuiz || currentModule.quiz) {
      const quizResult = quizResults[currentModule.id];
      if (quizResult && quizResult.passed) return null;
      return {
        id: "quiz",
        title: currentModule.quiz?.title || "Module Quiz",
        type: "quiz" as const,
        duration: currentModule.quiz?.timeLimit || 30,
        isPreview: false,
      };
    }
    return null;
  };

  const getPreviousLesson = () => {
    if (!currentModule || !currentLesson) return null;
    const currentModuleIndex = modules.findIndex(
      (m) => m.id === currentModule.id,
    );
    const currentLessonIndex = currentModule.lessons.findIndex(
      (l) => l.id === currentLesson.id,
    );
    if (currentLessonIndex > 0)
      return currentModule.lessons[currentLessonIndex - 1];
    if (currentModuleIndex > 0) {
      const prevModule = modules[currentModuleIndex - 1];
      const lastLesson = prevModule.lessons[prevModule.lessons.length - 1];
      if (lastLesson) return lastLesson;
      if (prevModule.hasQuiz || prevModule.quiz) {
        return {
          id: "quiz",
          title: prevModule.quiz?.title || "Module Quiz",
          type: "quiz" as const,
          duration: prevModule.quiz?.timeLimit || 30,
          isPreview: false,
        };
      }
    }
    return null;
  };

  const calculateProgress = () => {
    const totalLessons = modules.reduce((sum, m) => sum + m.lessons.length, 0);
    if (totalLessons === 0) return 0;
    return Math.round((completedLessons.size / totalLessons) * 100);
  };

  const progress = calculateProgress();
  const isCurrentCompleted = currentLesson
    ? completedLessons.has(currentLesson.id)
    : false;
  const nextLesson = getNextLesson();
  const prevLesson = getPreviousLesson();

  // Helper function to check if all lessons in a module are completed
  const areAllLessonsCompleted = (module: Module) => {
    return module.lessons.every((lesson) => completedLessons.has(lesson.id));
  };

  // Function to open module quiz in new tab
  const openModuleQuiz = (moduleId: string) => {
    const quizUrl = `/course/${id}/module/${moduleId}/take-quiz`;
    window.open(quizUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin md:w-12 md:h-12" />
          <p className="text-xs text-gray-500 md:text-sm">
            Loading your learning journey...
          </p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400 md:w-20 md:h-20" />
          <p className="mb-2 text-base text-gray-700 md:text-xl">
            Course not found
          </p>
          <Link
            to="/"
            className="text-sm text-orange-600 hover:underline md:text-base"
          >
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed inset-y-0 left-0 z-30 w-72 bg-white shadow-xl transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:w-64 xl:w-72 flex flex-col border-r border-gray-200`}
      >
        {/* Sidebar Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-white">
          <div className="flex items-center justify-between mb-3">
            <Link
              to={`/course/${id}`}
              className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-orange-600 transition-all duration-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Back to course</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className="text-sm font-bold text-gray-900 line-clamp-2">
            {course.title}
          </h2>

          {/* Gamification HUD */}
          <div
            onClick={() => setShowGamificationPanel(true)}
            className="p-3 mt-4 transition-all border border-orange-200 cursor-pointer bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl hover:border-orange-300"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20">
                  <Star className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">
                    Level {gamification.level}
                  </p>
                  <p className="text-xs font-bold text-gray-900">
                    {getLevelInfo(gamification.xp).current.title}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-lg">
                <Flame className="w-3 h-3 text-orange-500" />
                <span className="text-xs font-bold text-orange-600">
                  {gamification.streak}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500"
                  style={{
                    width: `${getLevelInfo(gamification.xp).progress}%`,
                  }}
                />
              </div>
              <span className="text-xs font-medium text-orange-600">
                {gamification.xp} XP
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Trophy className="w-3 h-3 text-amber-500" />
                {
                  gamification.achievements.filter((a) => a.unlockedAt).length
                }{" "}
                achievements
              </span>
              <ChevronRight className="w-3 h-3" />
            </div>
          </div>

          {/* Progress Section */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">Course Progress</span>
              <span className="text-sm font-bold text-orange-600">
                {progress}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out rounded-full bg-gradient-to-r from-orange-500 to-amber-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Module List */}
        <div className="flex-1 overflow-y-auto">
          {modules.map((module, mIndex) => {
            const completedInModule = module.lessons.filter((l) =>
              completedLessons.has(l.id),
            ).length;
            const moduleProgress =
              module.lessons.length > 0
                ? (completedInModule / module.lessons.length) * 100
                : 0;

            let moduleLocked = false;
            if (mIndex > 0) {
              const prevModule = modules[mIndex - 1];
              moduleLocked = !prevModule.lessons.every((l) =>
                completedLessons.has(l.id),
              );
            }

            return (
              <div key={module.id} className="border-b border-gray-200">
                <button
                  onClick={() => !moduleLocked && toggleModule(module.id)}
                  disabled={moduleLocked}
                  className={`flex items-center w-full gap-2 p-3 text-left transition-all duration-200 ${
                    moduleLocked
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-orange-50"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center flex-shrink-0 w-5 h-5 text-xs font-bold transition-transform rounded-lg ${
                      moduleLocked
                        ? "bg-gray-100 text-gray-400"
                        : "bg-gradient-to-br from-orange-100 to-amber-100 text-orange-600"
                    }`}
                  >
                    {mIndex + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-xs font-medium truncate ${moduleLocked ? "text-gray-400" : "text-gray-800"}`}
                      >
                        {module.title}
                      </p>
                      {moduleProgress === 100 && (
                        <CheckCircle className="flex-shrink-0 w-3.5 h-3.5 text-emerald-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-gray-500">
                        {module.lessons.length} lessons
                      </span>
                      {moduleProgress > 0 && moduleProgress < 100 && (
                        <div className="flex-1 max-w-16">
                          <div className="h-1 overflow-hidden bg-gray-200 rounded-full">
                            <div
                              className="h-full bg-orange-500 rounded-full"
                              style={{ width: `${moduleProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={`text-xs ${moduleLocked ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {moduleLocked ? (
                      <span className="text-xs text-amber-500">Locked</span>
                    ) : expandedModules.has(module.id) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </button>

                {expandedModules.has(module.id) && (
                  <div className="pb-3 space-y-0.5">
                    {module.lessons.map((lesson, lIndex) => {
                      const isCompleted = completedLessons.has(lesson.id);
                      const isCurrent = currentLesson?.id === lesson.id;
                      let isLocked = moduleLocked;
                      if (!isLocked && lIndex > 0)
                        isLocked = !completedLessons.has(
                          module.lessons[lIndex - 1].id,
                        );

                      return (
                        <button
                          key={lesson.id}
                          onClick={() =>
                            !isLocked && handleLessonSelect(lesson, module)
                          }
                          disabled={isLocked}
                          className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-all duration-200
                            ${isLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-50"}
                            ${isCurrent ? "bg-orange-50 border-l-2 border-orange-500" : "border-l-2 border-transparent"}
                          `}
                        >
                          <div className="flex-shrink-0 w-5">
                            <LessonIcon
                              type={lesson.type}
                              isCompleted={isCompleted}
                              isCurrent={isCurrent && !isCompleted}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs truncate ${
                                isCurrent
                                  ? "text-orange-600 font-medium"
                                  : isCompleted
                                    ? "text-gray-500"
                                    : isLocked
                                      ? "text-gray-400"
                                      : "text-gray-700"
                              }`}
                            >
                              {lesson.title}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {isLocked
                                ? "Locked"
                                : isCompleted
                                  ? "Completed"
                                  : formatDuration(lesson.duration)}
                            </p>
                          </div>
                          {isCompleted && !isCurrent && (
                            <CheckCircle className="flex-shrink-0 w-3.5 h-3.5 text-emerald-500" />
                          )}
                        </button>
                      );
                    })}

                    {(module.hasQuiz || module.quiz) && (
                      <button
                        onClick={() => {
                          const allLessonsCompleted = module.lessons.every(
                            (l) => completedLessons.has(l.id),
                          );
                          if (!allLessonsCompleted && !moduleLocked) {
                            showInfo(
                              "Complete all lessons in this module to unlock the quiz.",
                            );
                            return;
                          }
                          const quizUrl = `/course/${id}/module/${module.id}/take-quiz`;
                          window.open(
                            quizUrl,
                            "_blank",
                            "width=900,height=700,resizable=yes,scrollbars=yes",
                          );
                        }}
                        disabled={moduleLocked}
                        className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-all duration-200
                          ${moduleLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-50"}
                        `}
                      >
                        <div className="flex-shrink-0 w-5">
                          {quizResults[module.id]?.passed ? (
                            <Trophy className="w-4 h-4 text-emerald-500" />
                          ) : quizResults[module.id]?.score !== undefined ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <Trophy className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 truncate">
                            {module.quiz?.title || "Module Quiz"}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {module.quiz?.questions?.length || 0} questions •{" "}
                            {module.quiz?.timeLimit || 30} min
                            {quizResults[module.id]?.score !== undefined && (
                              <span
                                className={`ml-2 font-medium ${quizResults[module.id].passed ? "text-emerald-500" : "text-red-500"}`}
                              >
                                • {Math.round(quizResults[module.id].score)}%
                              </span>
                            )}
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sidebar Footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-orange-500" />
              <span>
                {completedLessons.size} /{" "}
                {modules.reduce((s, m) => s + m.lessons.length, 0)} completed
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500" />
              <span>Keep going!</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <div className="relative z-20 flex items-center flex-shrink-0 h-12 gap-3 px-3 bg-white border-b border-gray-200 md:px-4 md:h-14">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 text-gray-600 transition-all duration-200 rounded-lg hover:bg-gray-100 lg:hidden"
          >
            <Menu className="w-4 h-4" />
          </button>

          <div className="flex-1 min-w-0">
            <h1 className="flex items-center gap-1.5 text-xs font-medium text-gray-800 line-clamp-1 md:text-sm">
              <Sparkles className="flex-shrink-0 w-3.5 h-3.5 text-orange-500 md:w-4 md:h-4" />
              <span className="truncate">
                {currentLesson?.title || "Select a lesson"}
              </span>
            </h1>
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-1.5 p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <div className="flex items-center justify-center w-6 h-6 overflow-hidden rounded-full bg-gradient-to-br from-orange-500 to-amber-500">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt="Profile"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-xs font-semibold text-white">
                      {user?.firstName?.[0] || user?.email?.[0] || "U"}
                    </span>
                  )}
                </div>
                <ChevronDown className="w-3 h-3" />
              </button>
              {isProfileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 z-[100]">
                  <div className="px-4 py-2 mb-1 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.firstName
                        ? `${user.firstName} ${user.lastName || ""}`
                        : "User"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2.5 px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-3.5 h-3.5" /> Profile
                  </Link>
                  <Link
                    to="/dashboard/student"
                    className="flex items-center gap-2.5 px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <BookOpen className="w-3.5 h-3.5" /> Dashboard
                  </Link>
                  <Link
                    to="/dashboard/settings"
                    className="flex items-center gap-2.5 px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> Settings
                  </Link>
                  <div className="my-1 border-t border-gray-100" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 pt-3 md:p-6">
            {currentLesson ? (
              <div className="max-w-4xl mx-auto space-y-4 animate-fade-in md:space-y-6">
                {/* Quiz Launch Button */}
                {currentLesson.type === "quiz" &&
                  (currentModule?.quiz || currentQuiz) && (
                    <div className="p-6 text-center border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl md:p-8">
                      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 md:w-16 md:h-16">
                        <HelpCircle className="w-6 h-6 text-orange-600 md:w-8 md:h-8" />
                      </div>
                      <h3 className="mb-1 text-base font-bold text-gray-900 md:text-xl">
                        {currentQuiz?.title ||
                          currentModule?.quiz?.title ||
                          "Module Quiz"}
                      </h3>
                      <p className="mb-4 text-xs text-gray-500 md:text-sm">
                        {currentQuiz?.questions?.length ||
                          currentModule?.quiz?.questions?.length ||
                          0}{" "}
                        questions •{" "}
                        {currentQuiz?.timeLimit ||
                          currentModule?.quiz?.timeLimit ||
                          30}{" "}
                        minutes
                      </p>
                      <button
                        onClick={() => {
                          const quizModule = modules.find(
                            (m) => m.hasQuiz || m.quiz,
                          );
                          if (quizModule && id) {
                            const quizUrl = `/course/${id}/module/${quizModule.id}/take-quiz`;
                            window.open(
                              quizUrl,
                              "_blank",
                              "width=900,height=700,resizable=yes,scrollbars=yes",
                            );
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
                      >
                        Take Quiz
                      </button>
                    </div>
                  )}

                {/* Video Player */}
                {(currentLesson.type === "video" ||
                  currentLesson.type === "mixed") &&
                  currentLesson.videoUrl && (
                    <div>
                      <VideoPlayer
                        url={currentLesson.videoUrl}
                        title={currentLesson.title}
                      />
                      {currentLesson.videoTranscript && (
                        <div className="p-4 mt-4 border border-gray-200 bg-gray-50 rounded-xl md:mt-6 md:p-6">
                          <h3 className="flex items-center gap-2 mb-2 text-sm font-bold text-gray-900 md:text-base">
                            <FileText className="w-4 h-4 text-orange-500" />
                            Transcript
                          </h3>
                          <div className="text-xs leading-relaxed text-gray-600 whitespace-pre-wrap md:text-sm">
                            {currentLesson.videoTranscript}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {/* Content Section */}
                {currentLesson.content && currentLesson.type !== "quiz" && (
                  <div className="p-4 border border-gray-200 bg-gray-50 rounded-xl md:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 rounded-lg bg-orange-100">
                        <FileText className="w-4 h-4 text-orange-600" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 md:text-base">
                        Lesson Content
                      </h3>
                    </div>
                    <div className="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap md:text-sm">
                      {currentLesson.content}
                    </div>
                  </div>
                )}

                {/* Topics Section */}
                {currentLesson.topics && currentLesson.topics.length > 0 && (
                  <div className="space-y-3">
                    {currentLesson.topics.map((topic: any, idx: number) => (
                      <div
                        key={idx}
                        className="p-4 border border-gray-200 bg-gray-50 rounded-xl md:p-6"
                      >
                        <h3 className="mb-2 text-sm font-bold text-gray-900">
                          {topic.title}
                        </h3>
                        {topic.description && (
                          <p className="mb-3 text-xs text-gray-500">
                            {topic.description}
                          </p>
                        )}
                        {topic.paragraphs?.map((para: any, pIdx: number) => (
                          <p
                            key={pIdx}
                            className="mb-2 text-xs leading-relaxed text-gray-700"
                          >
                            {para.content}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Key Points */}
                {currentLesson.bullets &&
                  currentLesson.bullets.trim() &&
                  currentLesson.type !== "quiz" && (
                    <div className="p-4 border-l-4 border-orange-500 bg-gradient-to-r from-orange-50 to-amber-50 rounded-r-xl">
                      <h3 className="flex items-center gap-2 mb-3 text-sm font-bold text-gray-900">
                        <Zap className="w-4 h-4 text-orange-500" />
                        Key Takeaways
                      </h3>
                      <ul className="space-y-2">
                        {currentLesson.bullets
                          .split("\n")
                          .filter((b) => b.trim())
                          .map((bullet, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-xs text-gray-700"
                            >
                              <ChevronRight className="w-3.5 h-3.5 text-orange-500 shrink-0 mt-0.5" />
                              <span className="leading-relaxed">
                                {bullet.replace(/^[-•]\s*/, "")}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                {/* Navigation Buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-200">
                  {prevLesson ? (
                    <button
                      onClick={() => {
                        const prev = getPreviousLesson();
                        if (prev) {
                          const prevModule = modules.find((m) =>
                            m.lessons.some((l) => l.id === prev.id),
                          );
                          if (prevModule) handleLessonSelect(prev, prevModule);
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg bg-gray-800 hover:bg-gray-700 transition-all"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Previous</span>
                    </button>
                  ) : (
                    <div />
                  )}

                  {currentLesson.type !== "quiz" && (
                    <button
                      onClick={handleCompleteLesson}
                      disabled={completing || isCurrentCompleted}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium transition-all
                        ${
                          isCurrentCompleted
                            ? "bg-emerald-100 text-emerald-700 cursor-default"
                            : "bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:shadow-md"
                        } disabled:opacity-50`}
                    >
                      {completing ? (
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                      ) : isCurrentCompleted ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Done</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Complete</span>
                        </>
                      )}
                    </button>
                  )}

                  {nextLesson ? (
                    <button
                      onClick={async () => {
                        if (currentLesson?.type === "quiz") {
                          showSuccess("🎉 Quiz completed!");
                          return;
                        }
                        if (nextLesson.type === "quiz" || nextLesson.type !== "quiz") {
                          try {
                            setCompleting(true);
                            if (
                              currentLesson?.id &&
                              id &&
                              !completedLessons.has(currentLesson.id)
                            ) {
                              await enrollmentApi.completeLesson(
                                id,
                                currentLesson.id,
                              );
                              setCompletedLessons((prev) =>
                                new Set(prev).add(currentLesson.id),
                              );
                            }
                          } catch (err) {
                            console.error(err);
                          } finally {
                            setCompleting(false);
                          }
                        }
                        const next = getNextLesson();
                        if (next) {
                          setTimeout(() => {
                            const nextModule = modules.find(
                              (m) =>
                                m.lessons.some((l) => l.id === next.id) ||
                                (next.type === "quiz" && (m.hasQuiz || m.quiz)),
                            );
                            if (nextModule) handleLessonSelect(next, nextModule);
                            setExpandedModules(
                              (prev) => new Set([...prev, nextModule.id]),
                            );
                          }, 1500);
                        }
                      }}
                      disabled={completing}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
                    >
                      <span className="hidden sm:inline">
                        {completedLessons.has(currentLesson?.id)
                          ? "Next Lesson"
                          : "Finish & Next"}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          setCompleting(true);
                          if (
                            currentLesson?.id &&
                            id &&
                            !completedLessons.has(currentLesson.id)
                          ) {
                            await enrollmentApi.completeLesson(
                              id,
                              currentLesson.id,
                            );
                            setCompletedLessons((prev) =>
                              new Set(prev).add(currentLesson.id),
                            );
                          }
                          window.location.href = `/course/${id}/finish`;
                        } catch (err) {
                          console.error(err);
                          window.location.href = `/course/${id}/finish`;
                        } finally {
                          setCompleting(false);
                        }
                      }}
                      disabled={completing}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-md disabled:opacity-50"
                    >
                      Finish Course
                      <Trophy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <BookOpen className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    Select a lesson to start learning
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Choose from the sidebar to begin your journey
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* MODULE QUIZ BUTTON - ADDED AT BOTTOM */}
        {currentModule && (currentModule.hasQuiz || currentModule.quiz) && (
          <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white/95 backdrop-blur-sm md:p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100">
                    <Trophy className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">
                      Module {modules.findIndex(m => m.id === currentModule.id) + 1} Quiz
                    </p>
                    <p className="text-xs font-medium text-gray-800">
                      {currentModule.quiz?.title || `${currentModule.title} Quiz`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500">Required to pass</p>
                    <p className="text-xs font-semibold text-amber-600">
                      {currentModule.quiz?.passingScore || 70}% to pass
                    </p>
                  </div>
                  <button
                    onClick={() => openModuleQuiz(currentModule.id)}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 hover:shadow-md transition-all whitespace-nowrap"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    Take Module Quiz
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Completion status */}
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="flex items-center justify-between text-[9px] text-gray-400">
                  <span>Complete all lessons to unlock quiz</span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className={`w-3 h-3 ${areAllLessonsCompleted(currentModule) ? "text-emerald-500" : "text-gray-300"}`} />
                    {areAllLessonsCompleted(currentModule) ? "Ready to take quiz" : `${currentModule.lessons.filter(l => completedLessons.has(l.id)).length}/${currentModule.lessons.length} lessons completed`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gamification Notifications */}
      {showAchievement && (
        <AchievementNotification
          achievement={showAchievement}
          onClose={() => setShowAchievement(null)}
        />
      )}
      {showLevelUp && (
        <LevelUpNotification
          level={showLevelUp.level}
          title={showLevelUp.title}
          onClose={() => setShowLevelUp(null)}
        />
      )}
      {showStreak !== null && (
        <StreakNotification
          streak={showStreak}
          onClose={() => setShowStreak(null)}
        />
      )}
      {showGamificationPanel && (
        <GamificationPanel
          gamification={gamification}
          onClose={() => setShowGamificationPanel(false)}
        />
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0,0,0,0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #f97316; border-radius: 10px; }
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        @keyframes slide-up { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}